import axios from "axios";
import Vue from "vue";
import userStore from "@/store/user/current";

const URL_BACKEND = process.env.VUE_APP_URL_BACKEND;

/**
 * Récupère l'access token depuis les cookies
 */
function getAccessToken() {
  return Vue.$cookies.get("access_token");
}

/**
 * Récupère le refresh token depuis les cookies
 */
function getRefreshToken() {
  return Vue.$cookies.get("refresh_token");
}

/**
 * Définit un nouvel access token dans le cookie
 */
function setAccessToken(token) {
  Vue.$cookies.set("access_token", token);
}

/**
 * Supprime les tokens du cookie
 */
function removeTokens() {
  Vue.$cookies.remove("access_token");
  Vue.$cookies.remove("refresh_token");
}

/**
 * Intercepteur : Ajoute l'Authorization: Bearer <access_token> à chaque requête si présent
 */
axios.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = "Bearer " + token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Intercepteur : Gère les erreurs 4xx, tente un refresh si possible, etc.
 */
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Gère l'absence de `error.response` (ex: souci réseau)
    if (!error.response) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const path = new URL(originalRequest.url).pathname;
    const status = error.response.status;

    // Si c'est le refresh endpoint qui échoue, on supprime directement les tokens
    if (path.endsWith("/api-token/refresh/")) {
      removeTokens();
      return Promise.reject(error);
    }

    // Tente un refresh si erreur 4xx (401, 403...) et qu'on n'a pas déjà réessayé
    if (status >= 400 && status < 500 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          // Pas de refresh token => impossible de rafraîchir
          userStore.state.retrieved = null;
          return Promise.reject(error);
        }

        // Appel pour rafraîchir le token
        const res = await axios.post(URL_BACKEND + "api-token/refresh/", {
          refresh: refreshToken,
        });

        if (res.status === 200) {
          // On stocke le nouvel access token et on relance la requête initiale
          setAccessToken(res.data.access);
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + res.data.access;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // Si le refresh échoue, on considère la session comme terminée
        userStore.state.retrieved = null;
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
